import { applySnapshot, clone, flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { MessageModel } from "../../components/common/message";
import { FileModel, IFileModel } from "../../components/fileupload/FileModel";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import {
  AgreementDetailModel,
  IAgreementDetailModel
} from "../agreement/AgreementModel";
import { CurrencyModel, PaginationModel } from "../share";
import { ILoanSourcesModel, LoanSourcesModel } from "../share/LoanModel";
import { PlanHeaderModel } from "../share/PlanModel";
import {
  ActualResultAgreementListModel,
  IActualResultAgreementListModel,
} from "./ActualResultAgreementModel";
import existingDebtPlanAPI from "./ExistingDebtPlanService";

const restructureArray: string[] = [
  "ROLL",
  "REF",
  "RORF",
  "CCS",
  "IRS",
  "COIR",
  "NA"
];

export const DebtSettlementInfoModel = types
  .model("DebtSettlementInfoModel", {
    loanAmount: customtypes.optional(types.number, 0),
    interestAmount: customtypes.optional(types.number, 0),
    feeAmount: customtypes.optional(types.number, 0),
    attatchFiles: customtypes.optional(types.array(FileModel), [])
  })
  .views((self: any) => ({
    //
  }))
  .actions((self: any) => ({
    onAddAttatchFiles: (file: File[]) => {
      file.forEach((_file: File) => {
        try {
          const newFileUpload: IFileModel = FileModel.create({});
          newFileUpload.setToBase64Format(_file);
          self.attatchFiles.push(newFileUpload);
        } catch (e) {
          console.log("onAddDebtsFile Error: ", e);
        }
      });
    },
    onRemoveAttatchFile: (file: IFileModel) => {
      const index = self.attatchFiles.findIndex(
        (_file: IFileModel) => _file.fileName === file.fileName
      );
      if (index >= 0) {
        self.attatchFiles.splice(index, 1);
      }
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IDebtSettlementInfoModel = typeof DebtSettlementInfoModel.Type;

export const OverAllSummaryModel = types
  .model("OverAllSummaryModel", {
    title: customtypes.optional(types.string, ""),
    value: customtypes.optional(types.number, 0),
    interestValue: customtypes.optional(types.number, 0),
    feeValue: customtypes.optional(types.number, 0),
    principalValue: customtypes.optional(types.number, 0)
  })
  .views((self: any) => ({
    get valueAsMillionBaht() {
      return self.value / 1000000;
    },
    get principalAsMillionBaht() {
      return self.principalValue / 1000000;
    },
    get interestAsMillionBaht() {
      return self.interestValue / 1000000;
    },
    get feeAsMillionBaht() {
      return self.feeValue / 1000000;
    }
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IOverAllSummaryModel = typeof OverAllSummaryModel.Type;

export const LoanSourcePlansModel = types
  .model("LoanSourcePlansModel", {
    title: customtypes.optional(types.string, ""),
    value: customtypes.optional(types.string, "")
  })
  .views((self: any) => ({
    // get createLogUsername() {
    //   return self.createLog ? self.createLog.userName || "-" : "-";
    // },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type ILoanSourcePlansModel = typeof LoanSourcePlansModel.Type;

export const CalcurateReferencesModel = types
  .model("CalcurateReferencesModel", {
    refLabel: customtypes.optional(types.string, ""),
    refType: customtypes.optional(types.string, ""),
    refValue: customtypes.optional(types.string, "")
  })
  .views((self: any) => ({
    //
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type ICalcurateReferencesModel = typeof CalcurateReferencesModel.Type;

export const InterestSavingModel = types
  .model("InterestSavingModel", {
    calculatedSaving: customtypes.optional(types.array(LoanSourcesModel), []),
    calcurateReferences: customtypes.optional(
      types.array(CalcurateReferencesModel),
      []
    )
  })
  .views((self: any) => ({
    get loanSourceTypeDomestic() {
      return self.calculatedSaving.find((item: ILoanSourcesModel) => {
        return item.sourceType === "L";
      });
    },
    get loanSourceTypeForeignDebt() {
      return self.calculatedSaving.filter(
        (item: ILoanSourcesModel) => item.sourceType !== "L"
      );
    }
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IInterestSavingModel = typeof InterestSavingModel.Type;

export const PaymentPlansModel = types
  .model("PaymentPlansModel", {
    isSelected: customtypes.optional(types.boolean, false),
    isRequestGuarantee: customtypes.optional(types.boolean, false),
    paymentPlanID: types.maybeNull(types.number),
    debtPaymentPlanType: customtypes.optional(types.string, ""),
    paymentSource: customtypes.optional(types.string, ""),
    loanSourcePlans: customtypes.optional(types.array(LoanSourcesModel), []),
    interestSaving: customtypes.optional(InterestSavingModel, {})
  })
  .views((self: any) => ({
    get loanSourceTypeDomestic() {
      return self.loanSourcePlans.find((item: ILoanSourcesModel) => {
        return item.sourceType === "L";
      });
    },
    get loanSourceTypeForeignDebt() {
      return self.loanSourcePlans.filter(
        (item: ILoanSourcesModel) => item.sourceType !== "L"
      );
    }
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setDefaultLoanSourcePlan: (
      debtPaymentPlanType: string,
      paymentSource: string,
      restructure?: boolean
    ) => {
      self.debtPaymentPlanType = debtPaymentPlanType;
      self.paymentSource = paymentSource ? paymentSource : "";
      self.loanSourcePlans.push({ sourceType: "L", currency: "THB" });
      self.interestSaving.calculatedSaving.push({
        sourceType: "L",
        currency: "THB"
      });
      self.interestSaving.calcurateReferences.push({
        refLabel: "คำอธิบาย",
        refType: "TEXT",
        refValue: ""
      });
      if (restructure) {
        self.loanSourcePlans.push({ sourceType: "F", currency: "USD" });
        self.interestSaving.calculatedSaving.push({
          sourceType: "F",
          currency: "USD"
        });
        self.interestSaving.calcurateReferences.push({
          refLabel: "คำอธิบาย",
          refType: "TEXT",
          refValue: ""
        });
      }
    },
    onAddLoanSourcePlan: () => {
      self.loanSourcePlans.push({ sourceType: "F", currency: "USD" });
      self.interestSaving.calculatedSaving.push({
        sourceType: "F",
        currency: "USD"
      });
      self.interestSaving.calcurateReferences.push({
        refLabel: "คำอธิบาย",
        refType: "TEXT",
        refValue: ""
      });
    },
    onAddLoanSourcePlanDomestic: () => {
      self.loanSourcePlans.push({ sourceType: "L", currency: "THB" });
      self.interestSaving.calculatedSaving.push({
        sourceType: "L",
        currency: "THB"
      });
      self.interestSaving.calcurateReferences.push({
        refLabel: "คำอธิบาย",
        refType: "TEXT",
        refValue: ""
      });
      self.loanSourcePlans.push({ sourceType: "F", currency: "USD" });
      self.interestSaving.calculatedSaving.push({
        sourceType: "F",
        currency: "USD"
      });
      self.interestSaving.calcurateReferences.push({
        refLabel: "คำอธิบาย",
        refType: "TEXT",
        refValue: ""
      });
    },
    onRemoveLoanSourcePlan: (index: number) => {
      self.loanSourcePlans.splice(index + 1, 1);
      self.interestSaving.calculatedSaving.splice(index + 1, 1);
      self.interestSaving.calcurateReferences.splice(index + 1, 1);
    }
  }));
export type IPaymentPlansModel = typeof PaymentPlansModel.Type;

export const ExistPlanAgreementModel = types
  .model("ExistPlanAgreementModel", {
    planExistID: customtypes.optional(types.number, 0),
    agreements: customtypes.optional(types.array(AgreementDetailModel), []),
    debtPaymentPlans: customtypes.optional(types.array(PaymentPlansModel), [])
  })
  .views((self: any) => ({
    get debtPaymentPlanIsSelectedList() {
      return self.debtPaymentPlans
        .filter((item: IPaymentPlansModel) => item.isSelected === true)
        .map((item: IPaymentPlansModel) => item.paymentPlanID);
    }
  }))
  .actions((self: any) => ({
    setAllField: (data: any) => {
      Object.keys(data).forEach((key) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on mapping key:", key);
        }
      });
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IExistPlanAgreementModel = typeof ExistPlanAgreementModel.Type;

export const ExistPlanAgreementListModel = types
  .model("ExistPlanAgreementListModel", {
    startYear: customtypes.optional(types.string, ""),
    planRelease: customtypes.optional(types.string, ""),
    planID: customtypes.optional(types.string, ""),
    projectType: customtypes.optional(types.string, ""),
    list: customtypes.optional(types.array(ActualResultAgreementListModel), []),
    paging: customtypes.optional(PaginationModel, {}),
    organizationID: types.maybe(types.number),
    error: customtypes.optional(ErrorModel, {}),
    loading: types.optional(types.boolean, false)
  })
  .views((self: any) => ({
    get existPlanAgreementListIdCheck() {
      return self.list
        .filter((item: IActualResultAgreementListModel) => item.isSelected)
        .map((item: IActualResultAgreementListModel) => item.planExistId);
    },
    get existPlanAgreementListlength() {
      return self.list.length;
    }
  }))
  .actions((self: any) => ({
    getExistPlanAgreementList: flow(function*() {
      try {
        self.loading = true;
        if (self.organizationID) {
          self.paging.pageSize = 999;
          const result = yield existingDebtPlanAPI.getExistPlansAgreementList({
            startYear: self.startYear,
            planRelease: self.planRelease,
            planID: self.planID,
            projectType: self.projectType,
            organizationID: self.organizationID,
            paging: self.paging
          });
          self.list = result.data.map((item: any) => ({
            ...item,
            planExistId: item.planExistId || item.planExistID || 0,
          }));
          self.setPagination(result);
        }
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    onSearchTextFilter: (value: any) => {
      self.setDefaultPagination();
      self.paging.searchText = value;
      self.getExistPlanAgreementList();
    },
    onSelectedFilter: (value: any) => {
      self.setDefaultPagination();
      self.planRelease = value;
      self.getExistPlanAgreementList();
    },
    setPagination: (result: any) => {
      self.paging.pageSize = result.pageSize;
      self.paging.pageNo = result.pageNo;
      self.paging.totalRow = result.totalRow;
    },
    onSelectedPage: (pageNo: number) => {
      self.paging.pageNo = pageNo;
      self.getExistPlanAgreementList();
    },
    setDefaultPagination: () => {
      self.paging.pageNo = 1;
    },
    resetAll: () => {
      self.list = [];
      self.paging = {};
    }
  }));

export type IExistPlanAgreementListModel = typeof ExistPlanAgreementListModel.Type;

export const PaymentPlanSummaryModel = types
  .model("PaymentPlanSummaryModel", {
    paymentPlans: customtypes.optional(types.array(PaymentPlansModel), []),
    paymentPlanSumAmountTHB: customtypes.optional(types.number, 0),
    error: types.optional(ErrorModel, {})
  })
  .views((self: any) => ({
    get paymentPlanSumAmountTHBAsMillionBaht() {
      return self.paymentPlanSumAmountTHB / 1000000;
    }
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IPaymentPlanSummaryModel = typeof PaymentPlanSummaryModel.Type;

export const PlanSummaryModel = types
  .model("PlanSummaryModel", {
    overAllSummary: customtypes.optional(types.array(OverAllSummaryModel), []),
    paymentPlanSummary: customtypes.optional(PaymentPlanSummaryModel, {}),
    restructurePlanSummary: customtypes.optional(PaymentPlanSummaryModel, {}),
    currencyData: customtypes.optional(CurrencyModel, {})
  })
  .views((self: any) => ({
    // get createLogUsername() {
    //   return self.createLog ? self.createLog.userName || "-" : "-";
    // },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IPlanSummaryModel = typeof PlanSummaryModel.Type;

export const AmountPaidModel = types
  .model("AmountPaidModel", {
    thb: customtypes.optional(types.number, 0)
  })
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IAmountPaidModel = typeof AmountPaidModel.Type;

export const actualResultsModel = types
  .model("actualResultsModel", {
    gfTransaction: customtypes.optional(types.string, ""),
    contractName: customtypes.optional(types.string, ""),
    externalReference: customtypes.optional(types.string, ""),
    counterparty: customtypes.optional(types.string, ""),
    type: customtypes.optional(types.string, ""),
    amountPaid: customtypes.optional(AmountPaidModel, {})
  })
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IActualResultsModel = typeof actualResultsModel.Type;

const PaySlipValueModel = types
  .model("PaySlipValueModel", {
    value: customtypes.optional(types.number, 0),
    unit: customtypes.optional(types.string, "")
  })
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));

export const actualResultTotalModel = types
  .model("actualResultTotalModel", {
    label: customtypes.optional(types.string, ""),
    level: customtypes.optional(types.number, 0),
    value: customtypes.optional(types.array(PaySlipValueModel), [])
  })
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IActualResultTotalModel = typeof actualResultTotalModel.Type;

export const actualResultSummaryModel = types
  .model("actualResultSummaryModel", {
    label: customtypes.optional(types.string, ""),
    level: customtypes.optional(types.number, 0),
    value: customtypes.optional(types.array(PaySlipValueModel), [])
  })
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IActualResultSummaryModel = typeof actualResultSummaryModel.Type;

export const actualResultPlanInfoModel = types
  .model("actualResultPlanInfoModel", {
    borrowDate: customtypes.optional(types.string, ""),
    repayDate: customtypes.optional(types.string, "")
  })
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IActualResultPlanInfoModel = typeof actualResultPlanInfoModel.Type;

export const PlanDetailsModel = types
  .model("PlanDetailsModel", {
    planExistID: customtypes.optional(types.number, 0),
    year: customtypes.optional(types.number, 0),
    agreementDetail: customtypes.optional(
      types.array(AgreementDetailModel),
      []
    ),
    paymentPlan: customtypes.optional(types.array(PaymentPlansModel), []),
    restructurePlan: customtypes.optional(types.array(PaymentPlansModel), []),
    paymentPlanSumAmount: customtypes.optional(
      types.array(LoanSourcesModel),
      []
    ),
    restructurePlanSumAmount: customtypes.optional(
      types.array(LoanSourcesModel),
      []
    ),
    debtPlanSumAmount: customtypes.optional(types.array(LoanSourcesModel), []),
    interestSavingSumAmount: customtypes.optional(
      types.array(LoanSourcesModel),
      []
    ),
    isNotRequiredApproval: customtypes.optional(types.boolean, false),
    actualResults: customtypes.optional(types.array(actualResultsModel), []),
    // ถ้า actualResultTotal จาก API เป็น single object ใช้บรรทัดนี้:
    actualResultTotal: customtypes.optional(actualResultTotalModel, {}),
    // ถ้า actualResultTotal จาก API เป็น array ใช้บรรทัดนี้:
    // actualResultTotal: customtypes.optional(types.array(actualResultTotalModel), []),
    actualResultSummary: customtypes.optional(types.array(actualResultSummaryModel), []),
    actualResultPlanInfo: customtypes.optional(actualResultPlanInfoModel, {})
  })
  .views((self: any) => ({
    get planDetailJSON() {
      return self.toJSON();
    },
    get loanSourceTypeSortByThaiFirst() {
      let loanSourceList = [];
      let loanSourceTypeL = [];
      let loanSourceTypeF = [];
      loanSourceTypeL = self.restructurePlanSumAmount.filter(
        (item: ILoanSourcesModel) => item.sourceType === "L"
      );
      loanSourceTypeF = self.restructurePlanSumAmount.filter(
        (item: ILoanSourcesModel) => item.sourceType === "F"
      );
      loanSourceList = [...loanSourceTypeL, ...loanSourceTypeF];
      return loanSourceList;
    }
    // get restructurePlanView() {

    // }
  }))
  .actions((self: any) => ({
    setAllField: (data: any) => {
      Object.keys(data).forEach((key, index) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on key", key, e);
          throw e;
        }
      });
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setDefaultPaymentPlan: () => {
      const paymentPlan1 = PaymentPlansModel.create({});
      paymentPlan1.setDefaultLoanSourcePlan("SCH", "04");
      self.paymentPlan.push(paymentPlan1);

      const paymentPlan2 = PaymentPlansModel.create({});
      paymentPlan2.setDefaultLoanSourcePlan("ADV", "04");
      self.paymentPlan.push(paymentPlan2);
    },
    setDefaultRestructurePlan: () => {
      const paymentPlan1 = PaymentPlansModel.create({});
      paymentPlan1.setDefaultLoanSourcePlan("ROLL", "L", true);
      self.restructurePlan.push(paymentPlan1);

      const paymentPlan2 = PaymentPlansModel.create({});
      paymentPlan2.setDefaultLoanSourcePlan("REF", "L", true);
      self.restructurePlan.push(paymentPlan2);

      const paymentPlan2a = PaymentPlansModel.create({});
      paymentPlan2a.setDefaultLoanSourcePlan("RORF", "L", true);
      self.restructurePlan.push(paymentPlan2a);

      const paymentPlan3 = PaymentPlansModel.create({});
      paymentPlan3.setDefaultLoanSourcePlan("CCS", "L", true);
      self.restructurePlan.push(paymentPlan3);

      const paymentPlan4 = PaymentPlansModel.create({});
      paymentPlan4.setDefaultLoanSourcePlan("IRS", "L", true);
      self.restructurePlan.push(paymentPlan4);

      const paymentPlan4a = PaymentPlansModel.create({});
      paymentPlan4a.setDefaultLoanSourcePlan("COIR", "L", true);
      self.restructurePlan.push(paymentPlan4a);

      const paymentPlan7 = PaymentPlansModel.create({});
      paymentPlan7.setDefaultLoanSourcePlan("NA", "L", true);
      self.restructurePlan.push(paymentPlan7);
    },
    setDefaultPaymentPlanSumAmount: () => {
      const loanSources = LoanSourcesModel.create({});
      loanSources.setField({ fieldname: "currency", value: "THB" });
      loanSources.setField({ fieldname: "sourceType", value: "L" });
      self.paymentPlanSumAmount.push(loanSources);
    },
    setDefaultRestructurePlanSumAmount: () => {
      const loanSourcesL = LoanSourcesModel.create({});
      const loanSourcesF = LoanSourcesModel.create({});
      loanSourcesL.setField({ fieldname: "currency", value: "THB" });
      loanSourcesL.setField({ fieldname: "sourceType", value: "L" });
      self.restructurePlanSumAmount.push(loanSourcesL);
      loanSourcesF.setField({ fieldname: "currency", value: "USD" });
      loanSourcesF.setField({ fieldname: "sourceType", value: "F" });

      self.restructurePlanSumAmount.push(loanSourcesF);
    },
    onAddAgreementDetail: (data: IAgreementDetailModel) => {
      self.agreementDetail.push(clone(data));
    },
    resetAll: () => {
      applySnapshot(self, {});
    }
  }));
export type IPlanDetailsModel = typeof PlanDetailsModel.Type;

export const ExistingDebtPlanModel = types
  .model("ExistingDebtPlanModel", {
    planHeader: types.optional(PlanHeaderModel, {}),
    planSummary: types.optional(PlanSummaryModel, {}),
    planDetails: customtypes.optional(types.array(PlanDetailsModel), []),
    debtSettlementInfo: types.optional(DebtSettlementInfoModel, {}),
    loading: types.optional(types.boolean, false),
    alert: types.optional(MessageModel, {}),
    error: types.optional(ErrorModel, {})
  })
  .views((self: any) => ({
    get planDetailsJSON() {
      const { planDetails, ...rest } = self.toJSON();
      return planDetails;
    },
    getPlanDetailsView(year: string): IPlanDetailsModel[] {
      return self.planDetails.filter((plan: IPlanDetailsModel) => {
        return plan.year === +year;
      });
    }
  }))
  .actions((self: any) => ({
    getExistingDebtPlan: flow(function*() {
      try {
        self.loading = true;
        const result = yield existingDebtPlanAPI.getExistingDebtPlan(
          self.planHeader.planID
        );
        self.setAllField(result.data);
        self.planDetails.forEach(function(item: IPlanDetailsModel) {
          if (item.paymentPlan.length === 0) {
            item.setDefaultPaymentPlan();
          }
          // if (item.restructurePlan.length === 0) {
          //   item.setDefaultRestructurePlan();
          // } else if (item.restructurePlan.length !== restructureArray.length) {
          const arr: any = [];
          restructureArray.forEach((r, index) => {
            const found = item.restructurePlan.find(
              (rp: any) => rp.debtPaymentPlanType === r
            );
            if (found) {
              arr.push(found);
            } else {
              const newPaymentPlan = PaymentPlansModel.create({});
              newPaymentPlan.setDefaultLoanSourcePlan(r, "L", true);
              arr.push(newPaymentPlan);
            }
          });
          item.restructurePlan = arr;
          // }
          if (item.paymentPlanSumAmount.length === 0) {
            item.setDefaultPaymentPlanSumAmount();
          }
          if (item.restructurePlanSumAmount.length === 0) {
            item.setDefaultRestructurePlanSumAmount();
          }
        });
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    createExistingDebtPlan: flow(function*() {
      try {
        const result = yield existingDebtPlanAPI.create({
          planRelease: self.planHeader.planRelease,
          startYear: self.planHeader.planYear,
          organizationID: self.planHeader.organization.id
        });
        self.planHeader.planID = result.data;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    updateExistingDebtPlanAgreement: flow(function*(data: IPlanDetailsModel) {
      try {
        self.loading = true;
        const temp = clone(self);
        temp.onAddAgreementDetailList(data);
        yield existingDebtPlanAPI.updateExistingDebtPlan(
          {
            planHeader: self.planHeader,
            planDetails: temp.planDetails,
            debtSettlementInfo: self.debtSettlementInfo
          },
          self.planHeader.planID
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกข้อมูลสำเร็จแล้วค่ะ";
        self.alert.message = "ข้อมูลถูกแก้ไขเรียบร้อยแล้ว";
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    updateExistingDebtPlan: flow(function*() {
      try {
        self.loading = true;
        yield existingDebtPlanAPI.updateExistingDebtPlan(
          {
            planHeader: self.planHeader,
            planDetails: self.planDetails,
            debtSettlementInfo: self.debtSettlementInfo
          },
          self.planHeader.planID
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกข้อมูลสำเร็จแล้วค่ะ";
        self.alert.message = "ข้อมูลถูกแก้ไขเรียบร้อยแล้ว";
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    deleteExistingDebtPlanAgreement: flow(function*(data: IPlanDetailsModel) {
      try {
        self.loading = true;
        const resultList = self.planDetails.filter(
          (element: IPlanDetailsModel) =>
            element.planExistID !== data.planExistID
        );
        yield existingDebtPlanAPI.updateExistingDebtPlan(
          {
            planHeader: self.planHeader,
            planDetails: resultList
          },
          self.planHeader.planID
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกสำเร็จค่ะ";
        self.alert.message = "ข้อมูลถูกลบเรียบร้อยแล้ว";
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    onAddAgreementDetailList: (data: IPlanDetailsModel) => {
      self.planDetails.push(clone(data));
    },
    setPlanId: (id: number) => {
      self.planHeader.planID = id;
    },
    setPlanYear: (playYear: number) => {
      self.planHeader.planYear = playYear;
    },
    setAllField: (data: any) => {
      Object.keys(data).forEach((key, index) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on key", key, e);
          throw e;
        }
      });
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    resetAll: () => {
      applySnapshot(self, {});
    }
  }));
export type IExistingDebtPlanModel = typeof ExistingDebtPlanModel.Type;
